import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "./sidebar.css";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Debug logging can be enabled for troubleshooting
  // console.log('Current user state:', user.role, user.permissions?.length);

  const allMenuItems = [
    { name: "Dashboard", icon: "/Images/dashboard.png", path: "/home", permission: "Dashboard" },
    {
      name: "Consultation",
      icon: "/Images/consultation.png",
      path: "/consultation",
      permission: "Consultation"
    },
    { name: "Patients", icon: "/Images/patients.png", path: "/patient", permission: "Patients" },
    {
      name: "Appointments",
      icon: "/Images/apointments.png",
      path: "/appointments",
      permission: "Appointments"
    },
    { name: "Nursing", icon: "/Images/nursing.png", path: "/nursing", permission: "Nursing" },
    { name: "Pathology", icon: "/Images/pathology.png", path: "/pathology", permission: "Pathology" },
    { name: "Pharmacy", icon: "/Images/pharmacy.png", path: "/pharmacy", permission: "Pharmacy" },
    { name: "Radiology", icon: "/Images/pathology.png", path: "/radiology", permission: "Radiology" },
    { name: "Store", icon: "/Images/pharmacy.png", path: "/store", permission: "Store" },
    {
      name: "Billing & Payments",
      icon: "/Images/billing.png",
      path: "/billing",
      permission: "Billing & Payments"
    },
    { name: "Reports", icon: "/Images/reports.png", path: "/reports", permission: "Reports" },
    { name: "User", icon: "/Images/user.png", path: "/user", permission: "User" },
    { name: "Settings", icon: "/Images/settings.png", path: "/settings", permission: "Settings" },
    { name: "Role", icon: "/Images/admin.png", path: "/master/role", permission: "Role" },
    { name: "Master", icon: "/Images/settings.png", path: "/master", permission: "Master" },
  ];

  // Helper function to check if user has permission or is Super Admin
  const hasPermission = (permission) => {
    // Super Admin has access to everything
    if (user.role === 'Super Admin') {
      return true;
    }
    
    // Admin should have access to specific modules only (as per screenshot)
    if (user.role === 'Admin') {
      const adminModules = [
        'Patients', 'Appointments', 'Nursing', 'Pathology', 
        'Pharmacy', 'Radiology', 'Store', 'Reports', 'User', 
        'Master'
      ];
      if (adminModules.includes(permission)) {
        return true;
      }
    }
    
    // Check permissions array if available
    if (user.permissions && Array.isArray(user.permissions)) {
      if (user.permissions.includes('SuperAdmin.All.Access')) {
        return true;
      }
      return user.permissions.includes(permission);
    }
    
    // Fallback for other roles - no access
    return false;
  };

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => {
    // If no permission specified, show the item
    if (!item.permission) {
      return true;
    }
    
    // Special handling for User management - only Admin and Super Admin can access
    if (item.permission === "User") {
      return user.role === 'Admin' || user.role === 'Super Admin';
    }
    
    // Special handling for Role management - only Super Admin can access
    if (item.permission === "Role") {
      return user.role === 'Super Admin';
    }
    
    // Check if user has permission for this menu item
    return hasPermission(item.permission);
  });

  // Navigate to first menu item when user permissions change
  useEffect(() => {
    if (menuItems.length > 0 && location.pathname === "/") {
      navigate(menuItems[0].path);
    }
  }, [user.role, user.permissions, menuItems, navigate, location.pathname]);

  const toggleSidebar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div 
        className="sidebar-toggle" 
        onClick={toggleSidebar}
        onTouchEnd={(e) => {
          e.preventDefault();
          toggleSidebar(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleSidebar(e);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <div className="toggle-icon-wrapper">
          {isCollapsed ? (
            <ChevronRight className="toggle-icon" />
          ) : (
            <ChevronLeft className="toggle-icon" />
          )}
        </div>
      </div>
      <nav>
        <ul>
          {menuItems.map((item, index) => (
            <li
              key={index}
              className={
                location.pathname.startsWith(item.path) ? "active" : ""
              }
            >
              <Link 
                to={item.path} 
                title={isCollapsed ? item.name : ""}
                onClick={(e) => {
                  // Prevent sidebar from expanding when clicking on links in collapsed state
                  e.stopPropagation();
                }}
              >
                <img src={item.icon} alt={`${item.name} icon`} />
                <span className={isCollapsed ? "hidden" : ""}>{item.name}</span>
              </Link>
              {index < menuItems.length - 1 && <hr />}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;